import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, Mail, Copy, Send, CheckCircle2, AlertCircle, Info, ArrowRight, Wallet, Users, Sparkles, TrendingUp, IndianRupee, MousePointer2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { referralApi, authApi, publicApi } from '../lib/api';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: 'spring', stiffness: 100 }
  }
};

export default function Referral() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [referrals, setReferrals] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [user, setUser] = useState(null);
  const [encashing, setEncashing] = useState(false);
  const [referralEnabled, setReferralEnabled] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = sessionStorage.getItem('token');
    
    try {
      // Always fetch public settings to check if referral is enabled
      const settingsRes = await publicApi.getSettings();
      setReferralEnabled(settingsRes.data.referral_enabled !== false);

      if (!token) {
        setFetching(false);
        return;
      }

      const [profileRes, statusRes] = await Promise.all([
        authApi.getProfile(),
        referralApi.getStatus()
      ]);
      setUser(profileRes.data);
      setReferrals(statusRes.data);
    } catch (error) {
      console.error('Failed to fetch referral data:', error);
      // Don't toast error if it's just a 401/not logged in
      if (error.response?.status !== 401) {
        toast.error('Failed to load personalized data');
      }
    } finally {
      setFetching(false);
    }
  };

  const handleSendReferral = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await referralApi.send(email);
      toast.success(`Referral sent to ${email}`);
      setEmail('');
      fetchData(); 
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send referral');
    } finally {
      setLoading(false);
    }
  };

  const handleEncash = async () => {
    if (!user || user.credits < 1000) return;
    
    setEncashing(true);
    try {
      await referralApi.encash();
      toast.success('Encashment request submitted successfully! Admin will contact you.');
      fetchData(); // Refresh to see deducted credits or status
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit encashment request');
    } finally {
      setEncashing(false);
    }
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  if (fetching) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <div className="relative inline-block">
            <Gift className="h-16 w-16 text-primary animate-bounce" />
            <Sparkles className="h-6 w-6 text-yellow-500 absolute -top-2 -right-2 animate-pulse" />
          </div>
          <p className="text-muted-foreground mt-4 font-medium">Preparing your rewards...</p>
        </div>
      </div>
    );
  }

  const referralCode = user?.referral_code || '';
  const referralLink = referralCode ? `${window.location.origin}/login?ref=${referralCode}` : '';

  if (!referralEnabled) {
    return (
      <div className="min-h-screen pt-20 pb-20 bg-[#050505] flex items-center justify-center px-6">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full glass-morphic rounded-[2rem] p-10 border border-white/10 text-center"
        >
          <div className="h-20 w-20 rounded-full bg-yellow-500/10 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="h-10 w-10 text-yellow-500" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-4">Program on Break</h2>
          <p className="text-zinc-400 mb-8 leading-relaxed">
            Our referral program is currently taking a short break while we make some improvements. Check back soon for new ways to earn!
          </p>
          <Button asChild className="rounded-full px-8">
            <Link to="/products">Browse Products</Link>
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-20 overflow-x-hidden bg-[#050505]">
      {/* Background Orbs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-[10%] right-[-5%] w-[30%] h-[30%] bg-secondary/10 rounded-full blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Hero Section */}
        <section className="py-16 md:py-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <span className="inline-block py-1 px-4 rounded-full bg-primary/20 text-primary text-xs font-bold tracking-widest uppercase mb-6 border border-primary/30">
              Reward Program
            </span>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-b from-white to-white/40">
              Refer Friends. <br />
              <span className="text-primary">You Earn 5% Credits.</span>
            </h1>
            <p className="text-xl text-zinc-400 max-w-2xl mx-auto mb-10 leading-relaxed">
              Invite your friends to Ved3dCreations. You earn <span className="text-white font-semibold">5% cashback credits</span> when referred users place eligible purchases. The referred customer does not earn this referral cashback on their own order.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl mx-auto mb-10">
              <div className="glass-panel rounded-2xl px-4 py-3">
                <p className="text-[11px] uppercase tracking-widest text-zinc-400">Reward</p>
                <p className="text-xl font-black text-white">5%</p>
              </div>
              <div className="glass-panel rounded-2xl px-4 py-3">
                <p className="text-[11px] uppercase tracking-widest text-zinc-400">Threshold</p>
                <p className="text-xl font-black text-white">₹1000</p>
              </div>
              <div className="glass-panel rounded-2xl px-4 py-3">
                <p className="text-[11px] uppercase tracking-widest text-zinc-400">Spendable</p>
                <p className="text-xl font-black text-white">100%</p>
              </div>
              <div className="glass-panel rounded-2xl px-4 py-3">
                <p className="text-[11px] uppercase tracking-widest text-zinc-400">Status</p>
                <p className="text-xl font-black text-white">Active</p>
              </div>
            </div>
            {!user && (
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button asChild size="lg" className="rounded-full px-8 h-14 text-lg font-bold shadow-[0_0_30px_rgba(139,92,246,0.4)] hover:shadow-[0_0_40px_rgba(139,92,246,0.6)] transition-all duration-300">
                  <Link to="/login">Get Started Now</Link>
                </Button>
                <Link to="/products" className="text-zinc-400 hover:text-white transition-colors flex items-center gap-2 font-medium">
                  Browse Products <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            )}
          </motion.div>
        </section>

        {/* How It Works */}
        <section className="py-20 border-y border-white/5">
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-12"
          >
            {[
              { 
                icon: MousePointer2, 
                title: 'Share the Link', 
                desc: 'Give your friends your unique referral link or code.',
                color: 'text-blue-500',
                bg: 'bg-blue-500/10'
              },
              { 
                icon: Users, 
                title: 'Friends Sign Up', 
                desc: 'They create an account using your unique referral link or code.',
                color: 'text-purple-500',
                bg: 'bg-purple-500/10'
              },
              { 
                icon: TrendingUp, 
                title: 'Earn Every Time', 
                desc: 'You receive 5% cashback credits whenever your referred users place eligible orders.',
                color: 'text-pink-500',
                bg: 'bg-pink-500/10'
              }
            ].map((step, i) => (
              <motion.div key={i} variants={itemVariants} className="relative group">
                <div className="mb-6 h-16 w-16 rounded-2xl flex items-center justify-center transition-all duration-500 group-hover:scale-110 group-hover:rotate-3 shadow-xl shadow-black/20 overflow-hidden">
                  <div className={`absolute inset-0 ${step.bg} opacity-50`} />
                  <step.icon className={`h-8 w-8 ${step.color} relative z-10`} />
                </div>
                <h3 className="text-2xl font-bold mb-3 text-white">{step.title}</h3>
                <p className="text-zinc-400 leading-relaxed">{step.desc}</p>
                <div className="absolute -z-10 top-0 left-0 w-full h-full bg-white/[0.02] rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
              </motion.div>
            ))}
          </motion.div>
        </section>

        {/* Rewards Breakdown */}
        <section className="py-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl font-bold mb-8">Unlimited Rewards. <br />Real Value.</h2>
              <div className="space-y-6">
                {[
                  { title: '5% Cashback Credits', desc: 'You get 5% back as credits on eligible purchases made by users you referred.' },
                  { title: 'Referrer-Only Reward', desc: 'Referral cashback is credited only to the referrer, not to the referred customer.' },
                  { title: 'Full Spending Power', desc: 'Credits you earn can be used to pay for products on Ved3dCreations.' },
                  { title: 'Encashment Facility', desc: 'Once your credits reach ₹1000, you can request to encash them directly to your bank.' }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="mt-1">
                      <div className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center">
                        <CheckCircle2 className="h-4 w-4 text-primary" />
                      </div>
                    </div>
                    <div>
                      <h4 className="font-bold text-white mb-1">{item.title}</h4>
                      <p className="text-zinc-400 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-[2rem] blur-3xl opacity-50" />
              <div className="glass-panel rounded-[2rem] p-10 border border-white/10 relative overflow-hidden surface-elevated">
                <div className="flex items-center justify-between mb-8">
                  <div className="h-14 w-14 rounded-2xl bg-white/5 flex items-center justify-center">
                    <Wallet className="h-7 w-7 text-primary" />
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-1">Encashment Threshold</p>
                    <p className="text-2xl font-bold text-white">₹1000.00</p>
                  </div>
                </div>
                
                <div className="space-y-6">
                  <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      whileInView={{ width: '65%' }}
                      viewport={{ once: true }}
                      transition={{ duration: 1.5, ease: "easeOut" }}
                      className="h-full bg-gradient-to-r from-primary to-secondary"
                    />
                  </div>
                  <div className="flex justify-between text-xs font-medium uppercase tracking-wider text-zinc-500">
                    <span>Your Journey</span>
                    <span>Goal: ₹1000</span>
                  </div>
                </div>

                <div className="mt-10 p-6 rounded-2xl bg-white/[0.03] border border-white/5 text-center">
                  <p className="text-sm text-zinc-400 mb-2">Current Conversion Rate</p>
                  <p className="text-3xl font-bold text-white">1 Credit = ₹1.00</p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Logged In Section */}
        {user ? (
          <motion.section 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            className="py-20 space-y-12"
          >
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Wallet Card */}
              <div className="lg:col-span-1 glass-panel rounded-3xl p-8 border border-white/10 flex flex-col justify-between relative overflow-hidden group surface-elevated">
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                  <IndianRupee className="h-24 w-24 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                    <Wallet className="h-5 w-5 text-primary" />
                    My Wallet
                  </h3>
                  <div className="mb-8">
                    <p className="text-sm text-zinc-500 font-medium mb-1">Available Credits</p>
                    <p className="text-5xl font-black text-white">₹{user.credits?.toFixed(2) || '0.00'}</p>
                  </div>
                </div>
                
                {user.credits >= 1000 ? (
                  <Button 
                    onClick={handleEncash} 
                    disabled={encashing}
                    className="w-full rounded-2xl h-14 font-bold bg-white text-black hover:bg-zinc-200 transition-all shadow-xl"
                  >
                    {encashing ? 'Processing...' : 'Encash Credits Now'}
                  </Button>
                ) : (
                  <div className="space-y-4">
                    <div className="flex justify-between text-xs font-bold text-zinc-500 uppercase tracking-wider">
                      <span>Progress</span>
                      <span>{Math.min(100, (user.credits / 1000) * 100).toFixed(0)}%</span>
                    </div>
                    <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-primary" style={{ width: `${Math.min(100, (user.credits / 1000) * 100)}%` }} />
                    </div>
                    <p className="text-xs text-zinc-500 leading-relaxed">
                      You need <span className="text-white font-bold">₹{(1000 - user.credits).toFixed(2)}</span> more to request encashment.
                    </p>
                  </div>
                )}
              </div>

              {/* Invite Card */}
              <div className="lg:col-span-2 glass-panel rounded-3xl p-8 border border-white/10 surface-elevated">
                <h3 className="text-xl font-bold mb-8 flex items-center gap-2">
                  <Send className="h-5 w-5 text-primary" />
                  Invite & Spread the Love
                </h3>

                <div className="grid md:grid-cols-2 gap-8 mb-10">
                  <div className="space-y-3">
                    <label className="text-xs font-bold text-zinc-500 uppercase tracking-widest">Your Referral Code</label>
                    <div className="flex items-center gap-2 p-4 bg-white/[0.03] border border-white/10 rounded-2xl group hover:border-primary/50 transition-colors">
                      <code className="text-xl font-mono font-black text-white flex-1">{referralCode || 'Not available yet'}</code>
                      <Button variant="ghost" size="icon" className="text-zinc-400 hover:text-white" onClick={() => copyToClipboard(referralCode)}>
                        <Copy className="h-5 w-5" />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <label className="text-xs font-bold text-zinc-500 uppercase tracking-widest">Invite Link</label>
                    <div className="flex items-center gap-2 p-4 bg-white/[0.03] border border-white/10 rounded-2xl group hover:border-primary/50 transition-colors">
                      <p className="text-sm font-medium text-white truncate flex-1">{referralLink}</p>
                      <Button variant="ghost" size="icon" className="text-zinc-400 hover:text-white" onClick={() => copyToClipboard(referralLink)}>
                        <Copy className="h-5 w-5" />
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="pt-8 border-t border-white/5">
                  <h4 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
                    <Mail className="h-4 w-4 text-primary" />
                    Quick Invite via Email
                  </h4>
                  <form onSubmit={handleSendReferral} className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                      <Input
                        type="email"
                        placeholder="Enter friend's email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-12 h-14 bg-white/[0.03] border-white/10 rounded-2xl focus:ring-primary focus:border-primary"
                        required
                      />
                    </div>
                    <Button 
                      type="submit" 
                      disabled={loading}
                      className="rounded-2xl bg-primary hover:bg-primary/90 h-14 px-8 font-bold"
                    >
                      {loading ? 'Sending...' : 'Send Invitation'}
                    </Button>
                  </form>
                </div>
              </div>
            </div>

            {/* Referrals Status */}
            <div className="glass-panel rounded-[2.5rem] p-10 border border-white/10 surface-elevated">
              <div className="flex items-center justify-between mb-10">
                <h3 className="text-2xl font-bold flex items-center gap-3">
                  <Users className="h-6 w-6 text-primary" />
                  Your Referrals
                </h3>
                <div className="flex items-center gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-green-500" />
                    <span className="text-zinc-500">Completed: {referrals.filter(r => r.status === 'completed').length}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-blue-500" />
                    <span className="text-zinc-500">Joined: {referrals.filter(r => r.status === 'registered').length}</span>
                  </div>
                </div>
              </div>

              {referrals.length === 0 ? (
                <div className="text-center py-20 border border-dashed border-white/10 rounded-3xl">
                  <div className="h-16 w-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-6">
                    <Users className="h-8 w-8 text-zinc-600" />
                  </div>
                  <h4 className="text-xl font-bold text-white mb-2">No referrals yet</h4>
                  <p className="text-zinc-500 max-w-sm mx-auto">
                    Share your link with friends to start earning credits when they purchase.
                  </p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {referrals.map((ref, i) => (
                    <motion.div 
                      key={i} 
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: i * 0.05 }}
                      className="p-5 rounded-2xl bg-white/[0.03] border border-white/5 flex items-center justify-between group hover:border-white/10 transition-colors"
                    >
                      <div className="overflow-hidden">
                        <p className="text-sm font-bold text-white truncate mb-1">{ref.email}</p>
                        <p className="text-[10px] text-zinc-500 uppercase tracking-widest font-bold">
                          Invited {new Date(ref.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <StatusBadge status={ref.status} />
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.section>
        ) : (
          /* CTA for Non-Logged In */
          <motion.section 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="py-20 text-center"
          >
            <div className="glass-morphic rounded-[3rem] p-16 border border-white/10 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-8 opacity-5">
                <Gift className="h-64 w-64 text-white" />
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to join the rewards program?</h2>
              <p className="text-lg text-zinc-400 max-w-xl mx-auto mb-10">
                Sign up to unlock referral benefits and use credits toward high-quality 3D printed creations.
              </p>
              <Button asChild size="lg" className="rounded-full px-12 h-16 text-xl font-black shadow-2xl hover:scale-105 transition-transform">
                <Link to="/login">Join the Program</Link>
              </Button>
            </div>
          </motion.section>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    pending: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
    registered: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
    completed: 'bg-green-500/10 text-green-500 border-green-500/20',
  };

  const labels = {
    pending: 'Sent',
    registered: 'Joined',
    completed: 'Reward Active'
  };

  return (
    <div className={`px-3 py-1 rounded-full border text-[10px] font-black uppercase tracking-widest ${styles[status] || ''}`}>
      {labels[status] || status}
    </div>
  );
}
